import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { S3Item } from 'types';
import { SearchResult } from 'types/api/search.type';

import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

import { productDataService } from 'services/products.service';

import { BucketContext } from 'contexts';

import { useCatalogueLabels } from 'hooks';

import { routes } from 'data/routes';

import { Loader } from 'components/Loader';
import { Sticker } from 'components/Sticker';

import * as S from './SearchBar.styles';

export function SearchBar(): React.ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { categoryLabel } = useCatalogueLabels();
  const { getUrl } = useContext(BucketContext);
  const [value, valueSet] = useState<string>('');
  const [results, resultsSet] = useState<SearchResult[]>([]);
  const [resultsWithImages, resultsWithImagesSet] = useState<
    Array<SearchResult & { imageUrl?: string }>
  >([]);
  const [isLoading, isLoadingSet] = useState<boolean>(false);
  const [isLoadingImages, isLoadingImagesSet] = useState<boolean>(false);
  const [isOpen, isOpenSet] = useState<boolean>(false);

  const { season, marketLine } = useAppSelector((state) => state.settings);
  const permissions = useAppSelector(getUserPermissions);

  const performSearch = useCallback(async () => {
    if (!value || value.trim().length === 0 || !season || !marketLine) {
      resultsSet([]);
      isOpenSet(false);
      return;
    }

    isLoadingSet(true);
    isOpenSet(true);

    try {
      const response = await productDataService.search({
        season,
        seasonLine: marketLine.toUpperCase(),
        q: value.trim(),
      });
      resultsSet(response.data || []);
    } catch (error) {
      console.error('Search error:', error);
      resultsSet([]);
      isLoadingImagesSet(false);
    } finally {
      isLoadingSet(false);
    }
  }, [value, season, marketLine]);

  useDebounce(
    () => {
      performSearch();
    },
    350,
    [value, season, marketLine]
  );

  useEffect(() => {
    if (!value || value.trim().length === 0) {
      isOpenSet(false);
      resultsSet([]);
      resultsWithImagesSet([]);
      isLoadingImagesSet(false);
    }
  }, [value]);

  useEffect(() => {
    if (results.length === 0) {
      resultsWithImagesSet([]);
      isLoadingImagesSet(false);
      return;
    }

    isLoadingImagesSet(true);

    const loadImages = async () => {
      const resultsWithUrls = await Promise.all(
        results.map(async (result) => {
          if (!result.imageName) {
            return { ...result, imageUrl: undefined };
          }

          try {
            const s3Item: S3Item = await getUrl(`catalogue/products${result.imageName}`);
            return { ...result, imageUrl: s3Item?.url };
          } catch (error) {
            console.error('Error loading image:', error);
            return { ...result, imageUrl: undefined };
          }
        })
      );

      resultsWithImagesSet(resultsWithUrls);
      isLoadingImagesSet(false);
    };

    loadImages();
  }, [results, getUrl]);

  const handleResultClick = (result: SearchResult) => {
    isOpenSet(false);
    valueSet('');

    const path = routes.productDetails({
      genre: result.nodeName,
      category: result.nodeName2.replace(/\//g, ' - '),
      id: result.id,
      itemCode: result.itemCode,
      mainFabricCode: result.mainFabricCode,
      itemColorCode: result.itemColorCode,
      genderCodeAndGenderDesc: result.genderCodeAndGenderDesc,
      styleCodeAndStyleDesc: result.styleCodeAndStyleDesc,
    });

    navigate(path);
  };

  const handleResultMouseDown = (e: React.MouseEvent, result: SearchResult) => {
    e.preventDefault();
    handleResultClick(result);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    valueSet(e.target.value);
  };

  return (
    <S.Wrapper data-testid="search-bar">
      <S.SearchInput
        placeholder="SEARCH..."
        name="search"
        autoComplete="off"
        value={value}
        onChange={handleInputChange}
        onFocus={performSearch}
        onBlur={() => {
          isOpenSet(false);
        }}
      />

      {isOpen && (
        <S.Results>
          <Loader isLoading={isLoading || isLoadingImages}>
            {(() => {
              const filteredResults = resultsWithImages.filter(
                (result) =>
                  permissions.includes('VIEW_INACTIVE_ITEMS') || result.active !== false
              );
              return filteredResults.length > 0 ? (
                <S.ResultsList>
                  {filteredResults.map((result) => (
                    <S.ResultItem
                      key={`${result.itemCode}-${result.itemColorCode}-${result.mainFabricCode}`}
                      onMouseDown={(e) => handleResultMouseDown(e, result)}
                    >
                      {result.imageUrl && (
                        <S.ResultImageWrapper>
                          <S.ResultImage
                            src={result.imageUrl}
                            alt={result.englishItemMainTitle}
                            loading="lazy"
                            $isInactive={result.active === false}
                          />
                          {result.active === false && (
                            <S.InactiveLayer>
                              <Sticker>{t('product.cancelled')}</Sticker>
                            </S.InactiveLayer>
                          )}
                        </S.ResultImageWrapper>
                      )}
                      <S.ResultInfo>
                        <S.ResultColumn>
                          <S.ResultCategory>
                            {result.genderCodeAndGenderDesc} {categoryLabel(result.nodeName2)}
                          </S.ResultCategory>
                        </S.ResultColumn>
                        <S.ResultColumn>
                          <S.ResultCode>
                            {result.genderCode} {result.itemCode} {result.mainFabricCode}{' '}
                            {result.itemColorCode}
                          </S.ResultCode>
                        </S.ResultColumn>
                        <S.ResultColumn>
                          <S.ResultColor>{result.mainFabricDesc}</S.ResultColor>
                        </S.ResultColumn>
                      </S.ResultInfo>
                    </S.ResultItem>
                  ))}
                </S.ResultsList>
              ) : !isLoading && !isLoadingImages ? (
                <S.NoResults>No results found</S.NoResults>
              ) : null;
            })()}
          </Loader>
        </S.Results>
      )}
    </S.Wrapper>
  );
}
